import { useMemo } from "react";
import dayjs from "dayjs";
import { parseDataKey } from "../utils/dateRange";
import { aggregateMinutes } from "../utils/aggregateMinutes";

/**
 * Enable logs:
 *   localStorage.setItem("DEBUG_TIO", "1"); location.reload();
 * Disable:
 *   localStorage.removeItem("DEBUG_TIO"); location.reload();
 */
const DEBUG_TIO =
  typeof window !== "undefined" && localStorage.getItem("DEBUG_TIO") === "1";

/* =========================
   HELPERS
========================= */

function miToHHmm(i) {
  const hh = String(Math.floor(i / 60)).padStart(2, "0");
  const mm = String(i % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

function toMinuteIndex(t) {
  if (!t) return 0;
  const [h, m] = String(t).split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
  return h * 60 + m;
}

function uiDateToKey(uiDate) {
  if (!uiDate) return null;
  const [y, m, d] = uiDate.split("-");
  return `${d}-${m}-${y}`;
}

function uiDateToDataDay(uiDate) {
  if (!uiDate) return null;
  const [y, m, d] = uiDate.split("-");
  const dt = dayjs(`${d}-${m}-${y}`, "DD-MM-YYYY", true);
  return dt.isValid() ? dt : null;
}

/* =========================
   🔑 SINGLE SOURCE OF TRUTH
   rawVideo → real active viewers
========================= */
function buildMinuteActive(rawVideoArr) {
  if (!Array.isArray(rawVideoArr)) return [];

  return rawVideoArr.map((v) => {
    const n = Number(v) || 0;
    const val = n / 2; // backend double-tick fix
    return Number.isFinite(val) && val > 0 ? val : 0;
  });
}

/* =========================
   TUNE-OUT CORE
========================= */

function buildMinuteTuneOut(tuneInArr, activeArr, debugMeta) {
  if (!Array.isArray(tuneInArr) || !Array.isArray(activeArr)) return [];

  const len = Math.min(tuneInArr.length, activeArr.length);
  if (len === 0) return [];

  const out = new Array(len).fill(0);
  out[0] = null;

  const rows = DEBUG_TIO ? [] : null;

  for (let i = 1; i < len; i++) {
    const tin = Number(tuneInArr[i]) || 0;
    const prevActive = Number(activeArr[i - 1]) || 0;
    const currActive = Number(activeArr[i]) || 0;

    const rawTout = tin + prevActive - currActive;

    let tout = rawTout;
    if (!Number.isFinite(tout)) tout = 0; // ⛔ DO NOT clamp negatives yet

    out[i] = Math.round(tout);

    if (rows) {
      rows.push({
        day: debugMeta?.dayKey ?? "",
        mi: i,
        time: miToHHmm(i),
        tuneIn: tin,
        activePrev: Number(prevActive.toFixed(2)),
        activeCurr: Number(currActive.toFixed(2)),
        deltaActive: Number((currActive - prevActive).toFixed(2)),
        rawTuneOut: Number(rawTout.toFixed(2)),
        tuneOut: out[i],
      });
    }
  }

  if (rows) {
    console.groupCollapsed(
      `TIO minute debug (${debugMeta?.label ?? "unknown"}) ${debugMeta?.dayKey ?? ""}`
    );
    console.table(rows.slice(0, 40));
    console.groupEnd();
  }

  return out;
}

function getWeekStartMonday(d) {
  const offset = (d.day() + 6) % 7;
  return d.subtract(offset, "day").startOf("day");
}

/* =========================
   MAIN HOOK
========================= */

export function useTuneInOutSeries({
  rawImpressions,
  rawVideo,
  sortedImpKeys,
  startDateInput,
  endDateInput,
  startTime,
  endTime,
  mode,
}) {
  return useMemo(() => {
    if (!rawImpressions || !rawVideo || !sortedImpKeys?.length) {
      return { tuneIn: [], tuneOut: [] };
    }

    /* =========================
       MINUTE MODE
    ========================= */

    if (mode === "minute") {
      const dayKey = uiDateToKey(startDateInput) || sortedImpKeys.at(-1);

      const tuneInDay = rawImpressions[dayKey] || [];
      const activeDay = buildMinuteActive(rawVideo[dayKey] || []);

      const tuneOutDay = buildMinuteTuneOut(tuneInDay, activeDay, {
        label: "minute-mode",
        dayKey,
      });

      const sm = toMinuteIndex(startTime);
      const em = toMinuteIndex(endTime);
      if (em < sm) return { tuneIn: [], tuneOut: [] };

      const tuneIn = [];
      const tuneOut = [];

      // index is the minute of the day, which is what lets the chart put clock
      // times on its axis instead of labelling whole hours only.
      for (let i = sm; i <= em; i++) {
        const label = miToHHmm(i);
        tuneIn.push({ index: i, label, value: Number(tuneInDay[i]) || 0 });
        tuneOut.push({ index: i, label, value: Number(tuneOutDay[i]) || 0 });
      }

      if (DEBUG_TIO) {
        console.groupCollapsed(`TIO summary (minute) ${dayKey}`);
        console.groupEnd();
      }

      return { tuneIn, tuneOut };
    }

    /* =========================
       BUILD PER-DAY BASE
    ========================= */

    const start = uiDateToDataDay(startDateInput);
    const end = uiDateToDataDay(endDateInput);

    // ✅ Validate dates are properly parsed
    if (!start || !end) {
      return { tuneIn: [], tuneOut: [] };
    }

    const dayEntries = [];

    sortedImpKeys.forEach((key) => {
      const d = parseDataKey(key);
      if (!d) return;
      if (d.isBefore(start, "day")) return;
      if (d.isAfter(end, "day")) return;

      const tin = rawImpressions[key] || [];
      const active = buildMinuteActive(rawVideo[key] || []);
      const tout = buildMinuteTuneOut(tin, active, {
        label: `${mode}-range`,
        dayKey: key,
      });

      dayEntries.push({ key, d, tin, tout });
    });

    if (!dayEntries.length) {
      return { tuneIn: [], tuneOut: [] };
    }

    /* =========================
       HOUR
    ========================= */

    if (mode === "hour") {
      const tuneIn = [];
      const tuneOut = [];

      dayEntries.forEach(({ d, tin, tout }) => {
        const inAgg = aggregateMinutes(tin, "hour");
        const outAgg = aggregateMinutes(tout, "hour");

        for (let h = 0; h < inAgg.length; h++) {
          const label = `${d.format("DD.MM")} ${String(h).padStart(2, "0")}:00`;

          tuneIn.push({ label, value: Math.round(inAgg[h] || 0) });
          tuneOut.push({ label, value: Math.round(outAgg[h] || 0) });
        }
      });

      return { tuneIn, tuneOut };
    }

    /* =========================
       DAY
    ========================= */

    if (mode === "day") {
      return {
        tuneIn: dayEntries.map(({ d, tin }) => ({
          label: d.format("DD.MM"),
          value: Math.round(tin.reduce((a, b) => a + (Number(b) || 0), 0)),
        })),
        tuneOut: dayEntries.map(({ d, tout }) => ({
          label: d.format("DD.MM"),
          value: Math.round(tout.reduce((a, b) => a + (Number(b) || 0), 0)),
        })),
      };
    }

    /* =========================
       WEEK
    ========================= */

    if (mode === "week") {
      const bucket = new Map();

      dayEntries.forEach(({ d, tin, tout }) => {
        const ws = getWeekStartMonday(d);
        const k = ws.format("YYYY-MM-DD");

        const sin = tin.reduce((a, b) => a + (Number(b) || 0), 0);
        const sout = tout.reduce((a, b) => a + (Number(b) || 0), 0);

        if (!bucket.has(k)) bucket.set(k, { ws, in: 0, out: 0 });
        const ref = bucket.get(k);
        ref.in += sin;
        ref.out += sout;
      });

      const rows = Array.from(bucket.values()).sort((a, b) =>
        a.ws.isBefore(b.ws) ? -1 : 1
      );

      return {
        tuneIn: rows.map((r) => ({
          label: r.ws.format("DD.MM"),
          value: Math.round(r.in),
        })),
        tuneOut: rows.map((r) => ({
          label: r.ws.format("DD.MM"),
          value: Math.round(r.out),
        })),
      };
    }

    /* =========================
       MONTH
    ========================= */

    if (mode === "month") {
      const bucket = new Map();

      dayEntries.forEach(({ d, tin, tout }) => {
        const mk = d.format("YYYY-MM");

        const sin = tin.reduce((a, b) => a + (Number(b) || 0), 0);
        const sout = tout.reduce((a, b) => a + (Number(b) || 0), 0);

        if (!bucket.has(mk))
          bucket.set(mk, { m: dayjs(mk, "YYYY-MM"), in: 0, out: 0 });

        const ref = bucket.get(mk);
        ref.in += sin;
        ref.out += sout;
      });

      const rows = Array.from(bucket.values()).sort((a, b) =>
        a.m.isBefore(b.m) ? -1 : 1
      );

      return {
        tuneIn: rows.map((r) => ({
          label: r.m.format("MM.YYYY"),
          value: Math.round(r.in),
        })),
        tuneOut: rows.map((r) => ({
          label: r.m.format("MM.YYYY"),
          value: Math.round(r.out),
        })),
      };
    }

    return { tuneIn: [], tuneOut: [] };
  }, [
    rawImpressions,
    rawVideo,
    sortedImpKeys,
    startDateInput,
    endDateInput,
    startTime,
    endTime,
    mode,
  ]);
}
